import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardCheck,
  Clock,
  FileText,
  GraduationCap,
  Lock,
  PlayCircle,
  Target,
} from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { api } from '@/lib/api';
import type { LearnerEnrollment, LearnerOverview, ProgramDetail } from '@/types/api';
import { formatMinutes } from '@/lib/utils';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorState,
  LoadingState,
  Progress,
  Select,
} from '@/components/ui';

export function LearnPage() {
  const { t, language, pick } = useLanguage();
  const [selectedProgramId, setSelectedProgramId] = useState('');

  const overviewQuery = useQuery({
    queryKey: ['progress', 'overview'],
    queryFn: () => api.get<LearnerOverview>('/progress/overview'),
  });

  // Un agent peut suivre plusieurs parcours : un selecteur apparait alors.
  const enrollmentsQuery = useQuery({
    queryKey: ['progress', 'enrollments'],
    queryFn: () => api.get<{ enrollments: LearnerEnrollment[] }>('/progress/enrollments'),
  });

  const enrollments = enrollmentsQuery.data?.enrollments ?? [];
  const programId = selectedProgramId || overviewQuery.data?.enrollment?.programId;

  const programQuery = useQuery({
    queryKey: ['catalog', 'program', programId],
    queryFn: () => api.get<{ program: ProgramDetail }>(`/catalog/programs/${programId}`),
    enabled: Boolean(programId),
  });

  if (overviewQuery.isLoading || programQuery.isLoading) return <LoadingState />;
  if (overviewQuery.isError) return <ErrorState onRetry={() => void overviewQuery.refetch()} />;

  if (!overviewQuery.data?.enrollment) {
    return (
      <>
        <PageHeader title={t('learn.myPath')} />
        <Card>
          <EmptyState
            icon={<GraduationCap className="h-6 w-6" />}
            title={t('dashboard.notEnrolled')}
            description={t('dashboard.notEnrolledBody')}
          />
        </Card>
      </>
    );
  }

  if (programQuery.isError || !programQuery.data) return <ErrorState onRetry={() => void programQuery.refetch()} />;

  const program = programQuery.data.program;
  const finalExam = program.quizzes[0];
  const allModulesComplete = program.modules.every((module) => module.progressPercent === 100);

  return (
    <>
      <PageHeader
        title={pick(program, 'title')}
        description={pick(program, 'summary')}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {enrollments.length > 1 ? (
              <Select
                value={programId ?? ''}
                onChange={(event) => setSelectedProgramId(event.target.value)}
                aria-label={t('learn.choosePath')}
                className="w-auto"
              >
                {enrollments.map((enrollment) => (
                  <option key={enrollment.id} value={enrollment.programId}>
                    {pick(enrollment.program, 'title')}
                  </option>
                ))}
              </Select>
            ) : null}
            <Badge tone="primary" dot>
              {Math.round(program.enrollment?.progressPercent ?? 0)}% · {t('dashboard.overallProgress')}
            </Badge>
          </div>
        }
      />

      <div className="space-y-5">
        {program.modules.map((module) => (
          <Card key={module.id} className="overflow-hidden">
            <div className="flex flex-col gap-4 border-b border-border bg-surface-muted/50 p-5 sm:flex-row sm:items-center">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-ink">
                <span className="font-display text-lg font-bold">{module.order}</span>
              </span>

              <div className="min-w-0 flex-1">
                <h2 className="font-display text-lg font-semibold text-ink">{pick(module, 'title')}</h2>
                <p className="mt-0.5 line-clamp-2 text-sm text-ink-muted">{pick(module, 'summary')}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-subtle">
                  <span className="inline-flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" aria-hidden />
                    {t('learn.sessionsCount', { count: module.sessions.length })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" aria-hidden />
                    {formatMinutes(module.durationMinutes, language)}
                  </span>
                </div>
              </div>

              {module.locked ? (
                <Badge tone="warning" className="shrink-0">
                  <Lock className="h-3.5 w-3.5" aria-hidden />
                  {t('learn.locked')}
                </Badge>
              ) : null}

              <div className="w-full shrink-0 sm:w-40">
                <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-ink-muted">
                  <span>
                    {module.completedSessions}/{module.sessions.length}
                  </span>
                  <span>{Math.round(module.progressPercent)}%</span>
                </div>
                <Progress value={module.progressPercent} size="sm" tone={module.progressPercent === 100 ? 'success' : 'primary'} />
              </div>
            </div>

            <CardBody className="space-y-4">
              {module.locked ? (
                <p className="flex items-start gap-2 rounded-xl bg-warning-soft px-4 py-3 text-sm text-warning">
                  <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  {t('learn.lockedHint', {
                    count: (module.missingPrerequisites ?? []).length,
                    modules: (module.missingPrerequisites ?? []).map((entry) => pick(entry, 'title')).join(', '),
                  })}
                </p>
              ) : null}

              {module.objectivesFr.length > 0 ? (
                <details className="group rounded-xl border border-border p-4">
                  <summary className="cursor-pointer list-none text-sm font-semibold text-ink">
                    <span className="inline-flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" aria-hidden />
                      {t('learn.objectives')}
                      <ChevronRight className="h-4 w-4 text-ink-subtle transition-transform group-open:rotate-90" aria-hidden />
                    </span>
                  </summary>
                  <ul className="mt-3 space-y-1.5 pl-6 text-sm text-ink-muted">
                    {(language === 'en' ? module.objectivesEn : module.objectivesFr).map((objective) => (
                      <li key={objective} className="list-disc marker:text-primary">
                        {objective}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}

              <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                {module.sessions.map((session) => {
                  const status = session.progress?.status ?? 'NOT_STARTED';
                  return (
                    <li key={session.id}>
                      {/* Un module verrouille presente ses sessions sans lien :
                          l'API les refuserait de toute facon. */}
                      <Link
                        to={module.locked ? '#' : `/learn/sessions/${session.id}`}
                        aria-disabled={module.locked}
                        onClick={(event) => {
                          if (module.locked) event.preventDefault();
                        }}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          module.locked ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-muted'
                        }`}
                      >
                        {status === 'COMPLETED' ? (
                          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" aria-hidden />
                        ) : status === 'IN_PROGRESS' ? (
                          <PlayCircle className="h-5 w-5 shrink-0 text-primary" aria-hidden />
                        ) : (
                          <Circle className="h-5 w-5 shrink-0 text-ink-subtle" aria-hidden />
                        )}

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-ink">
                            {module.order}.{session.order} — {pick(session, 'title')}
                          </p>
                          <p className="truncate text-xs text-ink-subtle">{pick(session, 'summary')}</p>
                        </div>

                        <span className="hidden shrink-0 text-xs text-ink-muted sm:block">
                          {formatMinutes(session.durationMinutes, language)}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden />
                      </Link>
                    </li>
                  );
                })}
              </ul>

              {module.quizzes.map((quiz) => (
                <Link
                  key={quiz.id}
                  to={`/quizzes/${quiz.id}`}
                  className="flex items-center gap-3 rounded-xl border border-dashed border-accent/50 bg-accent-soft px-4 py-3 transition-colors hover:border-accent"
                >
                  <ClipboardCheck className="h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{pick(quiz, 'title')}</p>
                    <p className="text-xs text-ink-muted">
                      {t('quiz.passingScore')} : {quiz.passingScore}%
                    </p>
                  </div>
                  <Button size="sm" variant="outline">
                    {t('learn.takeQuiz')}
                  </Button>
                </Link>
              ))}
            </CardBody>
          </Card>
        ))}

        {finalExam ? (
          <Card className={allModulesComplete ? 'border-primary/40' : ''}>
            <CardBody className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                  <GraduationCap className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <p className="font-display font-semibold text-ink">{pick(finalExam, 'title')}</p>
                  <p className="text-sm text-ink-muted">
                    {t('quiz.passingScore')} : {finalExam.passingScore}%
                    {finalExam.timeLimitMinutes ? ` · ${t('quiz.timeLimit')} : ${finalExam.timeLimitMinutes} min` : ''}
                  </p>
                </div>
              </div>
              <Link to={`/quizzes/${finalExam.id}`} className="shrink-0">
                <Button disabled={!allModulesComplete}>{t('learn.finalExam')}</Button>
              </Link>
            </CardBody>
          </Card>
        ) : null}
      </div>
    </>
  );
}
